import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { authKioskService } from '../services/authKioskService';

const KioskActivateAccount = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [pin, setPin] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accountExists, setAccountExists] = useState(false);

  const verifyPin = async () => {
    setError('');
    setMessage('');
    setAccountExists(false);
    setSubmitting(true);
    try {
      const res = await authKioskService.verifyPin(pin);
      setEmailHint(res.data?.emailMasked || '');
      setAccountExists(!!res.data?.accountHasPassword);
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'PIN not recognized');
    } finally {
      setSubmitting(false);
    }
  };

  const activate = async () => {
    setError('');
    setMessage('');
    setAccountExists(false);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authKioskService.activateAccount({ pin, email, password });
      setMessage(res.message || 'Account activated.');
      setStep(2);
    } catch (err) {
      if (err.response?.data?.account_exists) {
        setAccountExists(true);
      }
      setError(err.response?.data?.message || err.message || 'Activation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'background.default' }}>
      <Paper sx={{ p: 3, maxWidth: 480, width: '100%' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Create employee account</Typography>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step><StepLabel>Verify PIN</StepLabel></Step>
          <Step><StepLabel>Set password</StepLabel></Step>
          <Step><StepLabel>Done</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {accountExists && (
          <Alert severity="info" sx={{ mb: 2 }}>
            An account already exists.{' '}
            <Link component={RouterLink} to="/forgot-password">Reset your password</Link>
          </Alert>
        )}

        {step === 0 && (
          <>
            <TextField
              fullWidth
              size="small"
              label="Kiosk PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 10))}
              sx={{ mb: 2 }}
            />
            <Button fullWidth variant="contained" disabled={submitting || !pin} onClick={verifyPin}>
              {submitting ? 'Verifying…' : 'Continue'}
            </Button>
          </>
        )}

        {step === 1 && accountExists && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This account already has a password. For security, you cannot set a new password from the kiosk.
              Use the link below to receive a reset email.
            </Alert>
            {emailHint && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Email on file: {emailHint}
              </Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              component={RouterLink}
              to="/forgot-password"
            >
              Reset password via email
            </Button>
          </>
        )}

        {step === 1 && !accountExists && (
          <>
            {emailHint && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Email on file: {emailHint}
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button fullWidth variant="contained" disabled={submitting} onClick={activate}>
              {submitting ? 'Creating…' : 'Create account'}
            </Button>
          </>
        )}

        {step === 2 && (
          <Button fullWidth variant="contained" onClick={() => navigate('/login')}>
            Go to login
          </Button>
        )}

        <Typography variant="body2" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/time-tracking/kiosk">Back to kiosk</Link>
          {' · '}
          <Link component={RouterLink} to="/forgot-password">Forgot password</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default KioskActivateAccount;
